import { format } from 'date-fns';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import SmsLogDetailModal from './SmsLogDetailModal';

import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextSelect from '@/components/TextSelect';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import { TableCell, TableRow } from '@/elements/Table';
import FormColumns from '@/form/FormColumns';
import { SmsLog, SmsLogFilters, smsLogsAPI } from '@/services/smsLogsAPI';

const headerFields = ['Time', 'Phone', 'Type', 'Status', 'Message', ''];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'disabled', label: 'Disabled' },
];

// Common outbound SMS types (SmsType in graphy). "All" leaves the filter off.
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'sms.job_offer', label: 'Shift Offer' },
  { value: 'sms.job_offer_group', label: 'Shift Offer (Group)' },
  {
    value: 'sms.job_self_serve_confirmation',
    label: 'Self-Serve Confirmation',
  },
  { value: 'sms.hired_job', label: 'Hired Shift' },
  { value: 'sms.hired_job_lta', label: 'Hired Shift (Long-term)' },
  { value: 'sms.job_cancelled', label: 'Shift Cancelled' },
  { value: 'sms.worker_dismissed', label: 'Worker Dismissed' },
  { value: 'sms.worker_check_in', label: 'Check In' },
  { value: 'sms.worker_check_out', label: 'Check Out' },
];

const SmsLogs = () => {
  const [logs, setLogs] = useState<SmsLog[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [phoneInput, setPhoneInput] = useState('');
  const [filters, setFilters] = useState<SmsLogFilters>({});
  const [selectedLog, setSelectedLog] = useState<SmsLog | null>(null);

  // Debounce the phone search so we don't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      setFilters((prev) => ({
        ...prev,
        to_phone: phoneInput || undefined,
      }));
    }, 300);
    return () => clearTimeout(timer);
  }, [phoneInput]);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await smsLogsAPI.getSmsLogs({
        ...filters,
        page: currentPage,
        per_page: itemsPerPage,
      });
      if (response.success) {
        setLogs(response.data || []);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('[SmsLogs] Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSelectChange =
    (key: 'status' | 'sms_type') => (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setCurrentPage(1);
      setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    };

  const handlePageChange = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleNumberItemsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  return (
    <Page
      footer={
        logs.length > 0 && (
          <Paginator
            currentPage={currentPage}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={itemsPerPage}
          />
        )
      }
      size="full"
      title="SMS Logs"
    >
      {selectedLog && (
        <SmsLogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}

      <Card>
        <Card.Section>
          <FormColumns layout="tuple">
            <FormElement label="Phone Number">
              <input
                type="text"
                placeholder="Any format, e.g. (555) 555-0123"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                }}
              />
            </FormElement>
            <FormElement label="Type">
              <TextSelect
                value={filters.sms_type || ''}
                onChange={handleSelectChange('sms_type')}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextSelect>
            </FormElement>
            <FormElement label="Status">
              <TextSelect
                value={filters.status || ''}
                onChange={handleSelectChange('status')}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextSelect>
            </FormElement>
          </FormColumns>
        </Card.Section>

        <Table>
          <TableHeader fields={headerFields} />
          <tbody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  <Small>
                    {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                  </Small>
                </TableCell>
                <TableCell>
                  <Body weight="medium">{log.to_phone}</Body>
                </TableCell>
                <TableCell>
                  <Small>{log.sms_type || '-'}</Small>
                </TableCell>
                <TableCell>
                  <Small>{log.status}</Small>
                </TableCell>
                <TableCell>
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Small>{log.body}</Small>
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    a11yLabel="View full message"
                    appearance="outline"
                    size="sm"
                    label="View"
                    onClick={() => setSelectedLog(log)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell align="center" colSpan={headerFields.length}>
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
        {!loading && logs.length === 0 && (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No SMS logs found.</Small>
            </Stack>
          </Card.Section>
        )}
      </Card>
    </Page>
  );
};

export default SmsLogs;
